import { useCallback, useEffect, useRef, useState } from "react";
import type {
  SupportChatTransport,
  SupportConversation,
  SupportMessage,
} from "./types";
import {
  isAllowedAttachment,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  formatBytes,
} from "./attachments";

/** A file staged for the next message. */
export interface PendingAttachment {
  /** Local id — the file has no server identity until it finishes uploading. */
  key: string;
  fileName: string;
  /** Set once the upload completes; undefined while in flight. */
  uploadUuid?: string;
  uploading: boolean;
  error?: string;
}

interface UseSupportChatOptions {
  transport: SupportChatTransport;
  /** Only poll while the panel is visible — a closed widget costs nothing. */
  active: boolean;
  pollIntervalMs: number;
}

export interface UseSupportChatResult {
  conversation: SupportConversation | null;
  messages: SupportMessage[];
  /** True while the assistant is composing the first reply of a turn. */
  awaitingReply: boolean;
  sending: boolean;
  error: string | null;
  /** Files staged for the next message. */
  pending: PendingAttachment[];
  attachFiles: (files: FileList | File[]) => Promise<void>;
  removeAttachment: (key: string) => void;
  send: (body: string) => Promise<void>;
  requestHuman: () => Promise<void>;
  dismissError: () => void;
}

/**
 * Conversation state for the widget.
 *
 * Polls `getMessages(afterId)` on an interval while open. `afterId` is an
 * exclusive cursor, so each poll transfers only what is new — the transcript
 * is never re-fetched.
 *
 * Everything here is defensive about unmounting and overlap: the widget lives
 * in a persistent layout, so a user can open and close it repeatedly across
 * route changes, and an in-flight poll must never write into a stale render.
 */
export function useSupportChat({
  transport,
  active,
  pollIntervalMs,
}: UseSupportChatOptions): UseSupportChatResult {
  const [conversation, setConversation] = useState<SupportConversation | null>(
    null,
  );
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingAttachment[]>([]);

  // Refs, not state: these are read inside the polling interval, and putting
  // them in state would re-create the interval on every message.
  const lastIdRef = useRef(0);
  const conversationRef = useRef<SupportConversation | null>(null);
  const mountedRef = useRef(true);
  const pollingRef = useRef(false);
  // Mirrors `pending` so the async attach loop reads current state without
  // re-creating its callback on every file.
  const pendingRef = useRef<PendingAttachment[]>([]);
  /**
   * In-flight conversation lookup/creation.
   *
   * Opening the panel starts `resumeConversation`, and typing immediately
   * starts `send` -> `ensureConversation`. Both check
   * `conversationRef.current`, both see null, and both then create a
   * conversation — so the first message ended up in one while the poll read
   * the other, showing it twice until things settled. Sharing one promise
   * makes the second caller await the first instead of racing it.
   */
  const conversationPromiseRef = useRef<Promise<SupportConversation | null> | null>(
    null,
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Transcript order.
   *
   * Real messages sort by ascending server id. Optimistic messages carry a
   * NEGATIVE id, so a plain numeric sort would throw them to the top of the
   * conversation instead of the bottom — they are pinned last, in the order
   * they were typed, which is where the user expects to see what they just
   * sent.
   */
  const bySequence = (a: SupportMessage, b: SupportMessage) => {
    const aOpt = a.id < 0;
    const bOpt = b.id < 0;
    if (aOpt !== bOpt) return aOpt ? 1 : -1;
    // Optimistic ids are `-Date.now()`, so the message typed EARLIER has the
    // larger (less negative) id — comparing them like server ids would show
    // a burst of messages in reverse. Sort those by recency instead.
    if (aOpt && bOpt) return b.id - a.id;
    return a.id - b.id;
  };

  const mergeMessages = useCallback((incoming: SupportMessage[]) => {
    if (incoming.length === 0) return;
    setMessages((prev) => {
      const byId = new Map<number, SupportMessage>();
      for (const m of prev) byId.set(m.id, m);
      for (const m of incoming) {
        // Never let a copy WITHOUT attachments overwrite one that has them.
        // Different endpoints populate this field differently, and a single
        // response that omits it would otherwise make files vanish from a
        // message that plainly had them.
        const existing = byId.get(m.id);
        byId.set(
          m.id,
          existing?.attachments?.length && !m.attachments?.length
            ? { ...m, attachments: existing.attachments }
            : m,
        );
      }

      // Drop any optimistic placeholder the server has now echoed back.
      //
      // This has to happen HERE, not only in `send`, because the 3s poll can
      // fetch the real copy before `postMessage` resolves — and it often
      // does, since that request also generates the AI reply. Without this
      // the placeholder and the server's copy both render until the send
      // settles, showing the user their own message twice for several
      // seconds. Escalating messages made it worst, being the slowest turn.
      //
      // Matched on role + body: an optimistic row has no server id to
      // compare, and the body is exactly what was sent.
      const realUserBodies = new Set(
        [...byId.values()]
          .filter((m) => m.id > 0 && m.role === "user")
          .map((m) => m.body),
      );
      for (const [id, m] of byId) {
        if (id < 0 && realUserBodies.has(m.body)) byId.delete(id);
      }

      return [...byId.values()].sort(bySequence);
    });
    const highest = Math.max(...incoming.map((m) => m.id));
    if (highest > lastIdRef.current) lastIdRef.current = highest;
  }, []);

  /**
   * Resume the caller's most recent unfinished conversation, if there is one.
   *
   * Without this a page refresh silently abandons the thread: the widget
   * would open a brand-new conversation while an admin (or Slack) replies to
   * the old one, so the customer never sees the answer and support believes
   * they responded. That is the worst failure this widget can have, and it
   * happens on something as ordinary as reloading the page.
   *
   * Only `ai`, `escalated` and `admin` resume — `resolved` and `closed` are
   * finished, and reopening them would confuse a new question with an old
   * thread. The list comes back newest-activity first, so the first match is
   * the right one.
   */
  const resumeConversation = useCallback(async (): Promise<void> => {
    if (conversationRef.current) return;
    try {
      const existing = await transport.listConversations();
      const active = existing.find(
        (c) =>
          c.status === "ai" || c.status === "escalated" || c.status === "admin",
      );
      if (!active || !mountedRef.current) return;

      conversationRef.current = active;
      setConversation(active);

      // Load the transcript from the beginning — `lastIdRef` is still 0, so
      // this fetches everything, and subsequent polls continue from there.
      const history = await transport.getMessages(active.uuid);
      if (!mountedRef.current) return;
      mergeMessages(history);
    } catch {
      // Resume is best-effort. A failure here just means the next message
      // opens a fresh conversation, which is the old behaviour.
    }
  }, [transport, mergeMessages]);

  /** Create the conversation lazily — opening the panel shouldn't cost a row. */
  const ensureConversation = useCallback(async (): Promise<SupportConversation> => {
    if (conversationRef.current) return conversationRef.current;

    // Single-flight: whoever arrives first does the work, everyone else
    // awaits the same promise. Without this, resume and send race and each
    // creates its own conversation.
    if (!conversationPromiseRef.current) {
      conversationPromiseRef.current = (async () => {
        await resumeConversation();
        if (conversationRef.current) return conversationRef.current;
        const created = await transport.startConversation();
        conversationRef.current = created;
        if (mountedRef.current) setConversation(created);
        return created;
      })();
    }

    const result = await conversationPromiseRef.current;
    // Clear so a later failure can be retried rather than replaying a
    // rejected promise forever.
    conversationPromiseRef.current = null;
    if (!result) throw new Error("Could not start a conversation");
    return result;
  }, [transport, resumeConversation]);

  const poll = useCallback(async () => {
    const convo = conversationRef.current;
    if (!convo) return;
    // Skip if the previous poll is still in flight — a slow network must not
    // stack requests.
    if (pollingRef.current) return;
    pollingRef.current = true;
    try {
      const fresh = await transport.getMessages(convo.uuid, lastIdRef.current);
      if (!mountedRef.current) return;
      if (fresh.length > 0) {
        mergeMessages(fresh);
        // Any assistant/admin/system message means the turn resolved.
        if (fresh.some((m) => m.role !== "user")) setAwaitingReply(false);
      }
    } catch {
      // A failed poll is not worth showing — the next one will likely work,
      // and an error toast every few seconds on a flaky connection is worse
      // than silence. Send failures DO surface, because the user acted.
    } finally {
      pollingRef.current = false;
    }
  }, [transport, mergeMessages]);

  useEffect(() => {
    if (!active) return;
    // Pick up an unfinished conversation first, then poll immediately so a
    // reply that landed while the panel was closed appears at once rather
    // than after a full interval.
    void (async () => {
      // Resume only — do NOT create here. Opening the panel shouldn't cost a
      // conversation row, and creation is `ensureConversation`'s job so the
      // two paths can't both create one.
      await resumeConversation();
      await poll();
    })();
    const id = setInterval(() => void poll(), pollIntervalMs);
    return () => clearInterval(id);
  }, [active, poll, pollIntervalMs, resumeConversation]);

  /**
   * Stage files for the next message, uploading each immediately.
   *
   * Uploading on selection rather than on send means the wait happens while
   * the user is still typing, and a failure surfaces before they commit to
   * sending. Each file is validated first so an obvious mistake never costs a
   * round trip.
   */
  const attachFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setError(null);

      for (const file of list) {
        if (pendingRef.current.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
          setError(
            `You can attach up to ${MAX_ATTACHMENTS_PER_MESSAGE} files per message.`,
          );
          break;
        }
        if (!isAllowedAttachment(file.name)) {
          setError(`${file.name} isn't a file type we can accept.`);
          continue;
        }
        if (file.size > MAX_ATTACHMENT_BYTES) {
          setError(
            `${file.name} is ${formatBytes(file.size)} — the limit is ${formatBytes(MAX_ATTACHMENT_BYTES)}.`,
          );
          continue;
        }

        const key = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        const entry: PendingAttachment = {
          key,
          fileName: file.name,
          uploading: true,
        };
        pendingRef.current = [...pendingRef.current, entry];
        setPending(pendingRef.current);

        try {
          const uploadUuid = await transport.uploadFile(file);
          if (!mountedRef.current) return;
          pendingRef.current = pendingRef.current.map((p) =>
            p.key === key ? { ...p, uploadUuid, uploading: false } : p,
          );
        } catch {
          if (!mountedRef.current) return;
          pendingRef.current = pendingRef.current.map((p) =>
            p.key === key
              ? { ...p, uploading: false, error: "Upload failed" }
              : p,
          );
          setError(`We couldn't upload ${file.name}. Please try again.`);
        }
        setPending(pendingRef.current);
      }
    },
    [transport],
  );

  const removeAttachment = useCallback((key: string) => {
    pendingRef.current = pendingRef.current.filter((p) => p.key !== key);
    setPending(pendingRef.current);
  }, []);

  const send = useCallback(
    async (body: string) => {
      const trimmed = body.trim();
      // Files that finished uploading are sendable; ones still in flight or
      // failed are not.
      const ready = pendingRef.current
        .filter((p) => p.uploadUuid && !p.error)
        .map((p) => p.uploadUuid!);
      // A file on its own is a valid message.
      if ((!trimmed && ready.length === 0) || sending) return;
      if (pendingRef.current.some((p) => p.uploading)) return;
      setSending(true);
      setError(null);

      // Render the message and the typing indicator NOW, before any network
      // call. `postMessage` also generates the AI reply server-side, so it is
      // the slowest request in the app — waiting for it before showing what
      // the user just typed makes the whole widget feel broken.
      //
      // Negative ids can never collide with a real one (server ids are
      // positive and ascending) and are trivially identifiable for removal.
      // They must NOT be sorted numerically though — see `bySequence`.
      const optimisticId = -Date.now();
      const optimistic: SupportMessage = {
        id: optimisticId,
        uuid: `optimistic-${optimisticId}`,
        createdAt: new Date().toISOString(),
        role: "user",
        body: trimmed,
      };
      setMessages((prev) => [...prev, optimistic]);
      setAwaitingReply(true);

      try {
        const convo = await ensureConversation();
        const saved = await transport.postMessage(convo.uuid, trimmed, ready);
        if (!mountedRef.current) return;
        // One merge rule for every path — `mergeMessages` inserts the saved
        // copy and clears the placeholder, whether the poll got there first
        // or this did.
        mergeMessages([saved]);
        pendingRef.current = [];
        setPending([]);
        void poll();
      } catch {
        if (mountedRef.current) {
          // Take the placeholder back out — leaving it would imply the
          // message was sent when it wasn't.
          setMessages((prev) => prev.filter((m) => m.id !== optimisticId));
          setAwaitingReply(false);
          setError("We couldn't send that message. Please try again.");
        }
      } finally {
        if (mountedRef.current) setSending(false);
      }
    },
    [ensureConversation, transport, mergeMessages, poll, sending],
  );

  const requestHuman = useCallback(async () => {
    const convo = conversationRef.current;
    if (!convo) return;
    try {
      const updated = await transport.escalate(convo.uuid);
      if (!mountedRef.current) return;
      conversationRef.current = updated;
      setConversation(updated);
      setAwaitingReply(false);
      void poll();
    } catch {
      if (mountedRef.current) {
        setError("We couldn't reach support just now. Please try again.");
      }
    }
  }, [transport, poll]);

  const dismissError = useCallback(() => setError(null), []);

  return {
    conversation,
    messages,
    awaitingReply,
    sending,
    error,
    pending,
    attachFiles,
    removeAttachment,
    send,
    requestHuman,
    dismissError,
  };
}
